/**
 * Mock data volume store — the "0 / Some / Huge" switch.
 *
 * localStorage-backed so the choice survives reloads, with a tiny pub/sub so
 * the on-screen MockDataSwitch and every consumer (generators, the UniBox api)
 * re-read the volume live without a full page reload. Volume is a dev-only
 * affordance gated to superadmins in the Sales Intelligence tab — it never
 * reaches production users.
 *
 * `none` (internally) == the "0 data" option on the switch.
 */

export type MockVolume = 'none' | 'some' | 'huge';

/** Which feed backs the Sales Intelligence board: generated mock data or real n8n data. */
export type SalesDataSource = 'mock' | 'real';

const STORAGE_KEY = 'brainiacs_mock_volume';
const SOURCE_STORAGE_KEY = 'brainiacs_sales_data_source';
type Listener = (volume: MockVolume) => void;
type SourceListener = (source: SalesDataSource) => void;

const DEFAULT_VOLUME: MockVolume = 'some';
const DEFAULT_SOURCE: SalesDataSource = 'mock';

const isVolume = (value: unknown): value is MockVolume =>
  value === 'none' || value === 'some' || value === 'huge';

const isSource = (value: unknown): value is SalesDataSource =>
  value === 'mock' || value === 'real';

let current: MockVolume = readInitial();
let currentSource: SalesDataSource = readInitialSource();

// Module-level listener sets — the store is a process singleton.
const listeners = new Set<Listener>();
const sourceListeners = new Set<SourceListener>();

function readInitial(): MockVolume {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isVolume(raw)) return raw;
  } catch {
    // localStorage may be unavailable (SSR, privacy mode) — fall back silently.
  }
  return DEFAULT_VOLUME;
}

function readInitialSource(): SalesDataSource {
  try {
    const raw = localStorage.getItem(SOURCE_STORAGE_KEY);
    if (isSource(raw)) return raw;
  } catch {
    // Ignore unreadable storage — default to mock.
  }
  return DEFAULT_SOURCE;
}

export const mockDataVolumeStore = {
  getVolume(): MockVolume {
    return current;
  },

  setVolume(next: MockVolume): void {
    if (next === current) return;
    current = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore write failures — in-memory value still updates.
    }
    listeners.forEach((listener) => listener(next));
  },

  /** Current feed source for Sales Intelligence ('mock' | 'real'). */
  getSource(): SalesDataSource {
    return currentSource;
  },

  setSource(next: SalesDataSource): void {
    if (next === currentSource) return;
    currentSource = next;
    try {
      localStorage.setItem(SOURCE_STORAGE_KEY, next);
    } catch {
      // Ignore write failures — in-memory value still updates.
    }
    sourceListeners.forEach((listener) => listener(next));
  },

  /** Subscribe to volume changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Subscribe to data-source changes. Returns an unsubscribe function. */
  subscribeSource(listener: SourceListener): () => void {
    sourceListeners.add(listener);
    return () => {
      sourceListeners.delete(listener);
    };
  },
};
